import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { CheckCircle, Loader2, Circle, Languages } from "lucide-react";
import { getProgress } from "@/utils/api";

interface ProcessingStatusProps {
  videoId?: string;
  status?: string;
  progress?: number;
  isUploading?: boolean;
  uploadProgress?: number;
  sourceLanguage?: string;
  targetLanguage?: string;
  onSourceLanguageChange?: (language: string) => void;
  onTargetLanguageChange?: (language: string) => void;
  onStatusChange?: (status: string, progress: number) => void;
  onVideoStatusUpdate?: (videoStatus: any) => void; // 🚀 NEW: Pass full video status to parent
}

// 🚀 NEW: Interface for new schema
interface VideoStatus {
  preview: {
    status: string;
    progress: number;
    current_stage: string;
    s3_srt_url?: string;
    s3_video_url?: string;
    error_message?: string;
    translation_status: string;
    translation_error_message?: string;
    translation_count?: number;
  };
  full: {
    status: string;
    progress: number;
    current_stage: string;
    s3_srt_url?: string;
    s3_video_url?: string;
    error_message?: string;
    translation_status: string;
    translation_error_message?: string;
    translation_count?: number;
  };
  overall_status: string;
  progress: number;
  current_stage: string;
  upload_error_message?: string;
}

// 🚀 NEW: Status mapping for new schema
const getStatusDisplay = (status: string) => {
  switch (status) {
    case 'not_started':
      return { text: 'Not Started', color: 'text-gray-400' };
    case 'pending':
      return { text: 'Ready to Start', color: 'text-blue-600' };
    case 'processing':
      return { text: 'Processing', color: 'text-yellow-600' };
    case 'completed':
      return { text: 'Completed', color: 'text-green-600' };
    case 'failed':
      return { text: 'Failed', color: 'text-red-600' };
    default:
      return { text: 'Unknown', color: 'text-gray-500' };
  }
};

export default function ProcessingStatus({
  videoId,
  status: initialStatus,
  progress: initialProgress = 0,
  isUploading = false,
  uploadProgress = 0,
  sourceLanguage = 'auto',
  targetLanguage = '',
  onSourceLanguageChange,
  onTargetLanguageChange,
  onStatusChange,
  onVideoStatusUpdate
}: ProcessingStatusProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(initialStatus || 'uploaded');
  const [progress, setProgress] = useState(initialProgress);
  
  // 🚀 NEW: State for new schema
  const [videoStatus, setVideoStatus] = useState<VideoStatus | null>(null);
  const [previewStatus, setPreviewStatus] = useState('pending');
  const [fullStatus, setFullStatus] = useState('pending');
  const [previewProgress, setPreviewProgress] = useState(0);
  const [fullProgress, setFullProgress] = useState(0);
  const [shouldStopPolling, setShouldStopPolling] = useState(false);
  
  const [ws, setWs] = useState<WebSocket | null>(null);

  // Update local state when props change
  useEffect(() => {
    setStatus(initialStatus || 'uploaded');
    setProgress(initialProgress);
  }, [initialStatus, initialProgress]);

  // 🚀 NEW: Fetch status from API with new schema
  useEffect(() => {
    if (!videoId || isUploading) return;
    
    const fetchStatus = async () => {
      try {
        const response = await getProgress(videoId);
        console.log('📊 ProcessingStatus - API Response:', response);
        
        if (response.preview && response.full) {
          // Parse new schema
          setVideoStatus(response);
          setPreviewStatus(response.preview.status);
          setFullStatus(response.full.status);
          setPreviewProgress(response.preview.progress);
          setFullProgress(response.full.progress);
          
          // Overall status
          setStatus(response.overall_status);
          setProgress(response.progress);
          
          // 🚀 EXTENDED DEBUG LOGGING
          console.log('📊 ProcessingStatus - Status updated:', {
            preview: response.preview.status,
            full: response.full.status,
            overall: response.overall_status,
            previewProgress: response.preview.progress,
            fullProgress: response.full.progress,
            shouldStopPolling: response.preview.status === 'completed',
            videoStatus: response,
            setVideoStatus: 'called',
            setPreviewStatus: 'called',
            setFullStatus: 'called'
          });
          
          // Notify parent
          onStatusChange?.(response.overall_status, response.progress);
          
          // 🚀 NEW: Pass full video status to parent
          onVideoStatusUpdate?.(response);
          
          // 🚀 STOP POLLING if preview is completed (user can view preview)
          if (response.preview.status === 'completed') {
            console.log('✅ Preview completed - stopping polling for preview');
            setShouldStopPolling(true);
            return true; // Signal to stop polling
          }
          
          // 🚀 STOP POLLING if both preview and full are completed
          if (response.preview.status === 'completed' && response.full.status === 'completed') {
            console.log('✅ Both preview and full completed - stopping polling');
            setShouldStopPolling(true);
            return true; // Signal to stop polling
          }
        } else {
          // 🚨 ERROR: Expected new schema but received old format
          console.error('❌ API returned unexpected response format:', response);
          throw new Error('Invalid API response format');
        }
        return false; // Continue polling
      } catch (error) {
        console.error('❌ Failed to fetch status:', error);
        return false; // Continue polling on error
      }
    };
    
    // Initial fetch
    fetchStatus().then((shouldStop) => {
      if (shouldStop) return; // Don't start interval if already completed
    });
    
    // Only start polling if not already completed
    const interval = setInterval(async () => {
      // Check if we should stop polling
      if (shouldStopPolling) {
        clearInterval(interval);
        console.log('🛑 Polling stopped - flag set to stop');
        return;
      }
      
      const shouldStop = await fetchStatus();
      if (shouldStop) {
        clearInterval(interval);
        console.log('🛑 Polling stopped - job completed');
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [videoId, isUploading, onStatusChange]);

  useEffect(() => {
    // Only setup WebSocket if we have a videoId and not uploading
    if (!videoId || isUploading) return;
    
    // Setup WebSocket connection for real-time updates
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws?videoId=${videoId}`;
    
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      console.log('WebSocket connected');
      setWs(websocket);
    };

    websocket.onmessage = (event) => {
      try {
        const update = JSON.parse(event.data);
        if (update.video_id === videoId) {
          // Notify parent to refetch data
          onStatusChange?.(update.status, update.progress);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    websocket.onclose = () => {
      console.log('WebSocket disconnected');
      setWs(null);
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => {
      websocket.close();
    };
  }, [videoId, isUploading, onStatusChange]);

  // 🚀 NEW: Dynamic steps based on new schema
  const getSteps = () => {
    if (!videoStatus) {
      // Loading state
      return [
        { key: 'upload', label: t('processing.fileUpload'), status: 'completed' },
        { key: 'preview', label: 'Preview Processing (20s)', status: 'pending' },
        { key: 'full', label: 'Full Processing', status: 'pending' },
        { key: 'translation', label: 'Translation', status: 'pending' }
      ];
    }

    // New schema steps
    return [
      { 
        key: 'upload', 
        label: t('processing.fileUpload'), 
        status: isUploading ? 'processing' : 'completed' 
      },
      { 
        key: 'preview', 
        label: 'Preview Processing (20s)', 
        status: previewStatus === 'completed' ? 'completed' : previewStatus === 'processing' ? 'processing' : 'pending',
        progress: previewProgress,
        hasProgress: true
      },
      { 
        key: 'full', 
        label: 'Full Processing', 
        status: fullStatus === 'completed' ? 'completed' : fullStatus === 'processing' ? 'processing' : 'pending',
        progress: fullProgress,
        hasProgress: true
      },
      { 
        key: 'translation', 
        label: 'Translation', 
        status: videoStatus.preview.translation_status === 'completed' || videoStatus.full.translation_status === 'completed' ? 'completed' : 'pending' 
      }
    ];
  };

  const steps = getSteps();

  const getStepIcon = (stepStatus: string) => {
    switch (stepStatus) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Circle className="h-5 w-5 text-gray-300" />;
    }
  };

  const getStepStatusText = (stepStatus: string) => {
    switch (stepStatus) {
      case 'completed':
        return <span className="text-sm text-green-600 font-medium">{t('processing.complete')}</span>;
      case 'processing':
        return <span className="text-sm text-blue-600 font-medium">{t('processing.processing')}</span>;
      default:
        return <span className="text-sm text-gray-400 font-medium">{t('processing.pending')}</span>;
    }
  };

  const languages = [
    { code: 'auto', name: t('processing.autoDetect') },
    { code: 'en', name: t('languages.english') },
    { code: 'de', name: t('languages.german') },
    { code: 'fr', name: t('languages.french') },
    { code: 'es', name: t('languages.spanish') },
    { code: 'it', name: t('languages.italian') }
  ];

  // 🚀 NEW: Error message display component
  const ErrorMessage = ({ message, type }: { message?: string; type: 'upload' | 'preview' | 'full' | 'translation' }) => {
    if (!message) return null;
    
    return (
      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              {type === 'upload' && 'Upload Error'}
              {type === 'preview' && 'Preview Processing Error'}
              {type === 'full' && 'Full Processing Error'}
              {type === 'translation' && 'Translation Error'}
            </h3>
            <div className="mt-1 text-sm text-red-700">
              {message}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">
        {t('processing.title')}
      </h3>

      {/* Processing Steps */}
      <div className="space-y-6">
        {steps.map((step) => (
          <div key={step.key} className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              {getStepIcon(step.status)}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{step.label}</p>
              
              {/* 🚀 NEW: Error messages for each step */}
              {step.key === 'upload' && videoStatus?.upload_error_message && (
                <ErrorMessage message={videoStatus.upload_error_message} type="upload" />
              )}
              
              {step.key === 'preview' && videoStatus?.preview.error_message && (
                <ErrorMessage message={videoStatus.preview.error_message} type="preview" />
              )}
              
              {step.key === 'full' && videoStatus?.full.error_message && (
                <ErrorMessage message={videoStatus.full.error_message} type="full" />
              )}
              
              {step.key === 'translation' && (
                <>
                  {videoStatus?.preview.translation_error_message && (
                    <ErrorMessage message={videoStatus.preview.translation_error_message} type="translation" />
                  )}
                  {videoStatus?.full.translation_error_message && (
                    <ErrorMessage message={videoStatus.full.translation_error_message} type="translation" />
                  )}
                </>
              )}
              
              {/* Progress bars */}
              {step.key === 'upload' && (
                <div>
                  <p className="text-sm text-gray-500">Uploading video file...</p>
                  {step.status === 'processing' && (
                    <div className="mt-2">
                      <Progress value={uploadProgress} className="h-2" />
                      <p className="text-xs text-gray-500 mt-1">{uploadProgress}% complete</p>
                    </div>
                  )}
                </div>
              )}
              
              {step.key === 'preview' && step.hasProgress && (
                <div>
                  <p className="text-sm text-gray-500">Processing first 20 seconds...</p>
                  {step.status === 'processing' && (
                    <div className="mt-2">
                      <Progress value={step.progress || 0} className="h-2" />
                      <p className="text-xs text-gray-500 mt-1">{step.progress || 0}% complete</p>
                    </div>
                  )}
                </div>
              )}
              
              {step.key === 'full' && step.hasProgress && (
                <div>
                  <p className="text-sm text-gray-500">Processing full video...</p>
                  {step.status === 'processing' && (
                    <div className="mt-2">
                      <Progress value={step.progress || 0} className="h-2" />
                      <p className="text-xs text-gray-500 mt-1">{step.progress || 0}% complete</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            {getStepStatusText(step.status)}
          </div>
        ))}
      </div>

      {/* Processing Options */}
      <div className="pt-6 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2">
              {t('processing.sourceLanguage')}
            </Label>
            <Select value={sourceLanguage} onValueChange={onSourceLanguageChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2">
              {t('processing.targetLanguage')}
            </Label>
            <Select value={targetLanguage} onValueChange={onTargetLanguageChange}>
              <SelectTrigger>
                <SelectValue placeholder={t('processing.noTranslation')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">{t('processing.noTranslation')}</SelectItem>
                {languages.slice(1).map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}
